import { Injectable, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../../core/models/api-result';
import { HubsRailService } from './hubs-rail.service';
import { previewHubSummary } from './hub-page-preview.service';
import type { HubsOfficeHub, HubsOfficeTile, HubsRail, HubsRailItem, RailRsvp } from './hubs-rail.models';
import { previewVisitState } from '../coming-in/plan-visit-preview.service';

/**
 * THE EVERYTHING RAIL, WITHOUT A SESSION (`/preview/hubs`).
 *
 * Dean's 01f, as data: a BOOKER at Rum Runner Works (38 in today, three
 * faces, who is on Level 2), the design crit on Thursday with two more
 * behind it, and two things on now: a live sprint demo to Watch and a bake
 * sale in forty minutes to say Going to. The office card features Taco
 * Tuesday with its RSVP (Paper 12a, 2), and its day row and button follow
 * the Plan a visit harness (`?visit=`, coming-in/plan-visit-preview.service).
 *
 * Times are relative to the moment the harness loads, so LIVE is live and
 * "in 40m" is forty minutes away whenever it is opened. The office's hub is
 * the preview corpus's own Rum Runner Works (`bhm`).
 *
 * `?rail=quiet` empties this week and this hour, so the rail's "Coming up"
 * card (the soonest thing in the next month) can be looked at.
 *
 * `?office=bare` drops the office's photograph (and the hub's), and
 * `?office=broken` points it at a picture that does not exist, so the card's
 * fallback can be looked at in the harness.
 */

const MIN = 60_000;
const DAY = 86_400_000;

function at(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString();
}

/** The first `weekday` (0 Sunday … 6 Saturday) at least `minDays` after today, at h:m. */
export function nextWeekday(weekday: number, hours: number, minutes: number, minDays = 1): string {
  const d = new Date();
  d.setDate(d.getDate() + minDays);
  d.setDate(d.getDate() + ((weekday - d.getDay() + 7) % 7));
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

/** The next Thursday after today (or a week on, if today is Thursday), at h:m. */
function nextThursday(hours: number, minutes: number): string {
  const d = new Date();
  d.setDate(d.getDate() + ((4 - d.getDay() + 7) % 7 || 7));
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function item(partial: Partial<HubsRailItem> & Pick<HubsRailItem, 'id' | 'title' | 'startsAt'>): HubsRailItem {
  return {
    kind: 'HUB_EVENT',
    endsAt: null,
    isLive: false,
    actionLabel: 'RSVP',
    coverUrl: null,
    goingCount: null,
    watchingCount: null,
    hubId: null,
    hubName: null,
    where: null,
    ...partial,
  };
}

export function previewHubsRail(): HubsRail {
  return {
    hubsOfficeTile: {
      state: 'BOOKER',
      location: {
        // The building the preview hub page and heads-ups name (loc-rrw), so
        // the office card finds the building's heads-up.
        id: 'loc-rrw',
        name: 'Rum Runner Works',
        coverUrl: '/assets/hubs-preview/pro-exterior.jpg',
      },
      inToday: 38,
      capacityPct: 62,
      facesSample: [
        { _id: 'sara', name: 'Sara Jensen', picture: null },
        { _id: 'mike', name: 'Mike Osei', picture: null },
        { _id: 'lia', name: 'Lia Moreau', picture: null },
      ],
      floorsLine: 'Sara, Mike and Lia are on Level 2',
      myDesk: { date: at(0).slice(0, 10), label: 'Desk 2.14', note: 'Level 2 · today' },
      busiestDay: null,
      teamLine: null,
      teamPlaces: [],
      hubId: 'bhm',
      hubName: 'Rum Runner Works',
      hubCoverUrl: null,
      // Paper 12a, 2: the office card features an event, and its action is
      // the RSVP. A Spotlight event, so it answers through its Event widget.
      featuredEvent: item({
        id: 'sp-taco',
        kind: 'WORKPLACE_EVENT',
        title: 'Taco Tuesday',
        // Taco TUESDAY: the next Tuesday after today.
        startsAt: nextWeekday(2, 12, 30, 1),
        where: 'Ground floor kitchen · Spotlight',
        hubId: 'bhm',
        hubName: 'Rum Runner Works',
        goingCount: 24,
        interestedCount: 6,
        notGoingCount: 3,
        myResponse: 'GOING',
        rsvpEventId: null,
        rsvpMode: 'OPTIONAL',
        rsvpWidgetId: 'w-taco',
        spotlightId: 'sp-taco-article',
      }),
    },
    hubsWeekAhead: [
      item({
        id: 'wk-crit',
        title: 'Design crit: the new hubs home',
        startsAt: nextThursday(14, 0),
        goingCount: 12,
        hubId: 'rdx',
        hubName: 'RDx',
        actionLabel: 'RSVP',
      }),
      item({
        id: 'wk-run',
        title: 'Canal run',
        startsAt: nextThursday(12, 15),
        hubId: 'running',
        hubName: 'Running & Cycling',
      }),
      item({
        id: 'wk-games',
        kind: 'WORKPLACE_EVENT',
        title: 'Games night',
        startsAt: (() => {
          const d = new Date(Date.now() + 4 * DAY);
          d.setHours(19, 0, 0, 0);
          return d.toISOString();
        })(),
        where: 'Rum Runner Works',
      }),
    ],
    hubsOnNow: [
      item({
        id: 'now-demo',
        kind: 'LIVE',
        title: 'Sprint demo: hubs and desks',
        startsAt: at(-20 * MIN),
        endsAt: at(40 * MIN),
        isLive: true,
        actionLabel: 'Watch',
        coverUrl: '/assets/hubs-preview/pro-team.jpg',
        watchingCount: 18,
        hubId: 'rdx',
        hubName: 'RDx',
      }),
      item({
        id: 'now-bake',
        kind: 'WORKPLACE_EVENT',
        title: 'Bake sale for the canal run',
        startsAt: at(40 * MIN),
        actionLabel: 'RSVP',
        goingCount: 22,
        where: 'Rum Runner Works · Level 2 kitchen',
      }),
    ],
  };
}

/** The harness's quiet-week switch: `?rail=quiet`. */
export function previewQuietRail(): boolean {
  return typeof location !== 'undefined' && /[?&]rail=quiet\b/.test(location.hash + location.search);
}

/** The harness's office switch: `?office=bare` or `?office=broken`. */
export function previewOfficeCover(): 'photo' | 'bare' | 'broken' {
  if (typeof location === 'undefined') return 'photo';
  const match = /[?&]office=(bare|broken)\b/.exec(location.hash + location.search);
  return (match?.[1] as 'bare' | 'broken' | undefined) ?? 'photo';
}

/** The preview office with its cover as the switch asks. */
export function previewOffice(cover = previewOfficeCover()): HubsOfficeTile | null {
  const base = previewHubsRail().hubsOfficeTile;
  // Only a booked today holds the desk the tile names (?visit=, the Plan a visit harness).
  const tile = base && previewVisitState() !== 'booked' ? { ...base, myDesk: null } : base;
  if (!tile || cover === 'photo') return tile;
  const url = cover === 'broken' ? '/assets/hubs-preview/missing-cover.jpg' : null;
  return {
    ...tile,
    location: tile.location ? { ...tile.location, coverUrl: url } : null,
    hubCoverUrl: null,
  };
}

@Injectable()
export class HubsRailPreviewService extends HubsRailService {
  protected override async fetchOffice(): Promise<ApiResult<HubsOfficeTile | null>> {
    return ok(previewOffice());
  }

  protected override async fetchOfficeHub(hubId: string): Promise<ApiResult<HubsOfficeHub | null>> {
    const { purpose, resources } = previewHubSummary(hubId);
    return ok({ _id: hubId, name: 'Rum Runner Works', purpose, primaryColor: null, coverImage: null, logo: null, resources });
  }

  protected override async fetchWeek(): Promise<ApiResult<readonly HubsRailItem[] | null>> {
    return ok(previewQuietRail() ? [] : previewHubsRail().hubsWeekAhead);
  }

  protected override async fetchNow(): Promise<ApiResult<readonly HubsRailItem[] | null>> {
    return ok(previewQuietRail() ? [] : previewHubsRail().hubsOnNow);
  }

  /** The RSVP lands: no session to write to. */
  protected override async sendRsvp(item: HubsRailItem, response: RailRsvp): Promise<ApiResult<unknown>> {
    void item;
    void response;
    return ok(true);
  }

  /** A quiet week: one tasting at the office in seventeen days (dev's own shape). */
  protected override async fetchComingUp(): Promise<ApiResult<readonly HubsRailItem[] | null>> {
    // A FRIDAY tasting, a little over two weeks out.
    const d = new Date(nextWeekday(5, 16, 30, 14));
    return ok([
      item({
        id: 'wp-tasting',
        kind: 'WORKPLACE_EVENT',
        title: 'Friday tasting: three single origins from Rwanda',
        startsAt: d.toISOString(),
        goingCount: 9,
        hubId: 'bhm',
        hubName: 'Rum Runner Works',
        where: 'Rum Runner Works · Level 2 kitchen',
      }),
    ]);
  }

}

export const HUBS_RAIL_PREVIEW_PROVIDER: Provider = {
  provide: HubsRailService,
  useClass: HubsRailPreviewService,
};
